import { AlertTriangle, MapPin, Shield, Truck } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router'

import { ActionsSheet } from '@/components/ui/actions-sheet'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CopyButton } from '@/components/ui/CopyButton'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatDate } from '@/lib/utils'
import { useMarkLeadReadWhenUnread, useSalesLead } from '@/models/salesLead'
import { BuyerTypeSection } from './BuyerTypeSection'
import { CarrierEligibilityCard } from './CarrierEligibilityCard'
import { ContactsCard } from './ContactsCard'
import { EditLeadAddressDialog } from './EditLeadAddressDialog'
import { InlineEditField } from './InlineEditField'
import { LeadActionSidebar } from './LeadActionSidebar'
import { LeadAddress } from './LeadAddress'
import { LeadInsightsCard } from './LeadInsightsCard'
import { RegistrationRows } from './RegistrationRows'
import { SalesLeadDetailTabs } from './SalesLeadDetailTabs'

function SalesLeadDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>

      <div className="flex gap-6 max-lg:flex-col">
        <div className="flex max-w-5xl min-w-0 flex-1 flex-col gap-6">
          {/* Info cards */}
          <div className="grid gap-6 min-[1280px]:grid-cols-2 min-[1580px]:grid-cols-[1.5fr_1fr_1fr] min-[1280px]:[&>*:first-child]:col-span-2 min-[1580px]:[&>*:first-child]:col-span-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="gap-3">
                <CardHeader>
                  <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Skeleton key={j} className="h-4 w-full" />
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-24" />
              ))}
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        </div>

        {/* Action sidebar */}
        <div className="flex w-60 shrink-0 flex-col gap-3 max-lg:hidden">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    </div>
  )
}

interface SalesLeadDetailProps {
  // When rendered inside the pipeline drawer, the lead id comes from a prop
  // (and `onClose` closes the drawer) instead of the route param.
  leadId?: string
  onClose?: () => void
}

export function SalesLeadDetail({ leadId, onClose }: SalesLeadDetailProps = {}) {
  const params = useParams()
  const id = leadId ?? params.id
  const { data: lead, isLoading } = useSalesLead(id!)
  const [addressOpen, setAddressOpen] = useState(false)
  useMarkLeadReadWhenUnread(lead)

  if (isLoading || !lead) {
    return <SalesLeadDetailSkeleton />
  }

  return (
    <>
      {!onClose && (
        <Helmet>
          <title>{lead.legalName}</title>
        </Helmet>
      )}
      <div className="flex flex-col gap-6">
        {/* OOS Warning */}
        {lead.oosStatus && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <AlertTriangle className="size-5" />
            <div>
              <span className="font-medium">Out of Service</span> — This carrier cannot operate. Verify status
              before pursuing.
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold">
                <InlineEditField
                  leadId={lead.id}
                  field="legalName"
                  value={lead.legalName}
                  inputClassName="h-9 text-2xl font-semibold"
                  placeholder="Add legal name"
                />
              </h1>
              <CopyButton value={lead.legalName} />
              {lead.oosStatus && (
                <Badge variant="destructive">
                  <AlertTriangle className="mr-1 size-3" />
                  OOS
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground flex items-center gap-1">
              DOT:{' '}
              {lead.convertedOrg ? (
                <span>{lead.dotNumber || '—'}</span>
              ) : (
                <InlineEditField
                  leadId={lead.id}
                  field="dotNumber"
                  value={lead.dotNumber}
                  placeholder="Add DOT number"
                />
              )}
              {lead.dotNumber && <CopyButton value={lead.dotNumber} />}
              {lead.convertedOrg && (
                <Link
                  to={`/insure/internal/customers/${lead.convertedOrg}`}
                  className="text-xs underline-offset-2 hover:underline"
                >
                  Edit on customer
                </Link>
              )}
            </p>
          </div>
          <ActionsSheet label="Actions" title="Lead actions" className="ml-auto shrink-0">
            <LeadActionSidebar lead={lead} onClose={onClose} />
          </ActionsSheet>
        </div>

        <div className="flex gap-6 max-lg:flex-col">
          <div className="flex max-w-5xl min-w-0 flex-1 flex-col gap-6">
            {/* Info Cards */}
            <div className="grid gap-6 min-[1280px]:grid-cols-2 min-[1580px]:grid-cols-[1.5fr_1fr_1fr] min-[1280px]:[&>*:first-child]:col-span-2 min-[1580px]:[&>*:first-child]:col-span-1">
              <ContactsCard lead={lead} />

              <Card className="gap-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="size-4" />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <LeadAddress lead={lead} onEdit={() => setAddressOpen(true)} />
                </CardContent>
              </Card>

              <Card className="gap-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Truck className="size-4" />
                    Fleet Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Power Units:</span> {lead.powerUnits}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Drivers:</span> {lead.drivers}
                  </div>
                  <RegistrationRows lead={lead} />
                  <div>
                    <span className="text-muted-foreground">Hazmat:</span> {lead.hazmat ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Safety Rating:</span> {lead.safetyRating || 'N/A'}
                  </div>
                  {lead.cargoCarried.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Cargo:</span>{' '}
                      <span>{lead.cargoCarried.join(', ')}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="gap-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Shield className="size-4" />
                    Insurance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <BuyerTypeSection lead={lead} />
                  <div>
                    <span className="text-muted-foreground">Current Insurer:</span>{' '}
                    <InlineEditField
                      leadId={lead.id}
                      field="currentInsurer"
                      value={lead.currentInsurer}
                      placeholder="Unknown"
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground">Policy Expires:</span>{' '}
                    <InlineEditField
                      leadId={lead.id}
                      field="policyExpirationDate"
                      value={lead.policyExpirationDate}
                      inputType="date"
                      placeholder="Unknown"
                      displayValue={
                        <>
                          {lead.policyExpirationDate && formatDate(lead.policyExpirationDate)}
                          {lead.daysUntilExpiration != null && (
                            <span
                              className={cn(
                                'ml-2 font-medium',
                                lead.daysUntilExpiration > 30 && 'text-muted-foreground',
                                lead.daysUntilExpiration <= 30 && 'text-orange-600',
                                lead.daysUntilExpiration <= 14 && 'text-red-600'
                              )}
                            >
                              (
                              {lead.daysUntilExpiration < 0
                                ? `Expired ${Math.abs(lead.daysUntilExpiration)}d ago`
                                : `${lead.daysUntilExpiration}d left`}
                              )
                            </span>
                          )}
                        </>
                      }
                    />
                  </div>
                  {lead.cabLastFetched && (
                    <div className="text-muted-foreground mt-4">
                      Last updated from CAB: {new Date(lead.cabLastFetched).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>

              <CarrierEligibilityCard lead={lead} className="min-[1580px]:col-span-2" />

              <LeadInsightsCard
                lead={lead}
                className="bg-blue-50 min-[1280px]:col-span-2 min-[1580px]:col-span-3"
              />
            </div>

            <SalesLeadDetailTabs lead={lead} />
          </div>
          <div className="max-lg:hidden">
            <LeadActionSidebar lead={lead} onClose={onClose} />
          </div>
        </div>
      </div>
      <EditLeadAddressDialog open={addressOpen} onOpenChange={setAddressOpen} lead={lead} />
    </>
  )
}
